import { apiFetch } from './client';
import type { FolderSummary, FormSharing, Level } from './access';
import type { AccountBrand, FormLogo, GradientDir, LogoAlign } from '../shared/types';
import type { DeliverySummary, FieldDefinition, FormContinuation, FormLayout, FormRequiredStyle, FormRule, FormUploadSettings, FormWidget, FormAccess, ProviderProtocol, ProviderStatus, RuleOp, SubmissionFilters, Submitter, ViewConfig, FormSpamSettings, FormPrefillSettings } from '../shared/types';

// ---- types ----
export type FormStatus = 'draft' | 'published' | 'archived';

export interface FormThemeSettings {
  accent?: string;
  /** Second stop of the accent gradient (`--of-accent2`); absent = flat. */
  accent2?: string;
  /** How the pair runs; absent = diagonal. Inherits with the pair. */
  accentGradient?: GradientDir;
  bg?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  /** max width of the form card in px (blank = default 1600) */
  maxWidth?: number;
  // ---- custom branding (plan feature `customBranding`) ----
  /** The logo row — at most one per position. Read it through `themeLogos`. */
  logos?: FormLogo[];
  /** The single logo `logos` replaced; still rendered, folded in on read. */
  logo?: { url: string; height?: number; align?: LogoAlign };
  font?: { body?: string; heading?: string };
  mode?: 'light' | 'dark' | 'auto';
  presetId?: string;
}

/** What the respondent sees (or where they go) after submitting. */
export interface FormCompletion {
  /** 'message' shows the thank-you page; 'redirect' sends them to a URL. */
  mode?: 'message' | 'redirect';
  /** Heading on the thank-you page. */
  title?: string;
  /** Body of the thank-you page; supports {{fieldKey}} tokens. */
  body?: string;
  /** Where to go when mode is 'redirect' (tokens allowed). */
  redirectUrl?: string;
  /** Seconds to show the message before redirecting (0 = immediately). */
  redirectDelay?: number;
  /** Append ?submissionId=… to the redirect. */
  includeSubmissionId?: boolean;
  /** Offer a "submit another response" button on the thank-you page. */
  allowAnother?: boolean;
  /** Offer a PDF of the submission on the thank-you page (a signed, expiring link). */
  allowDocumentDownload?: boolean;
  /** Let the respondent edit their answers for `ttlHours` after submitting (P6). */
  allowEdit?: { ttlHours: number };
}

export interface FormScheduleSettings {
  opensAt?: string;
  closesAt?: string;
  maxSubmissions?: number;
  closedMessage?: string;
}

export interface FormSettingsView {
  hasPassword?: boolean;
  theme?: FormThemeSettings;
  schedule?: FormScheduleSettings;
  successMessage?: string;
  submitLabel?: string;
  completion?: FormCompletion;
  uploads?: FormUploadSettings;
  /** T4: delete submissions (and their files) older than this many days;
   *  absent = keep forever. Sending null on a save clears the window. */
  retention?: { days: number } | null;
  /** how required / optional fields are marked on the public form */
  requiredStyle?: FormRequiredStyle;
  /**
   * D15 (R9): a summary of what to fix at the top of the page after a failed
   * Continue or Submit, opt-in per form. Absent or false = the form as it was.
   */
  errorSummary?: boolean;
  /** bot protection (U6) */
  spam?: FormSpamSettings;
  /** which link parameters fill which questions (P1) */
  prefill?: FormPrefillSettings;
  /** who may open it — read here for the link's origin; the Access tab edits it through its own route */
  access?: Partial<FormAccess>;
  notifications?: { enabled?: boolean; to?: string[]; subject?: string };
  autoresponder?: { enabled?: boolean; emailField?: string; subject?: string; body?: string; includeAnswers?: boolean };
  /** These steps came from the old Settings → Emails switches; see the strip
   *  on Workflow. Cleared by the first save of the drawing. */
  migratedEmails?: boolean;
  previewPresets?: { id: string; name: string; flags: Record<string, boolean> }[];
  /** This form continues another one's submission (the order handoff). */
  continuation?: FormContinuation;
  /**
   * Hide the Form-Magic badge on the published form (plan feature
   * `hideAdvertisement`). Stored on every plan so the preference survives a
   * downgrade; only obeyed while the plan carries the feature.
   */
  hideAdvertisement?: boolean;
  /**
   * The one PDF this form hands out — attached to a workflow email, offered on
   * a thank-you page, downloaded from the submissions table. `template` is the
   * Smart PDF (plan feature `smartPdf`); anything else is the branded record,
   * which is free. Mirrors FormDocumentSettings on the server.
   */
  document?: {
    /**
     * DERIVED on the server from the template and the parts, and never sent:
     * no template and no parts is the record, a template alone is the
     * template, any parts at all are composed. Read it, do not write it.
     */
    mode?: 'record' | 'template' | 'composed';
    filename?: string;
    /** The PDFs, blank pages included, in upload order. Read through `templatesOf`. */
    templates?: FormPdfTemplate[];
    /** The one PDF of a document saved before there could be several — read, never written. */
    template?: FormPdfTemplate;
    /** The parts, in order — arranged on the Document tab. */
    blocks?: DocBlock[];
    /**
     * How long a download link keeps working, in days. `null` is "until the
     * submission is deleted"; absent is the default (30).
     */
    downloadTtlDays?: number | null;
  };
}

/**
 * Who may open a form, resolved by GET /admin/forms — the provider's NAME
 * lives on the provider, not the form, so the list route joins it.
 */
export interface FormListAccess {
  mode: FormAccess['mode'];
  providerName: string | null;
  domains: string[];
  requirements: { claim: string; op: string; value: string }[];
}

export interface FormSummary {
  id: number;
  name: string;
  slug: string;
  status: FormStatus;
  settings: FormSettingsView;
  submission_count: number;
  last_submission_at: string | null;
  has_draft: boolean;
  /** Set while the form is not collecting: kept, but its public URL renders
   *  the closed state and it does not count against the plan's form limit. */
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
  /** null = Unfiled, which is a state rather than a folder row (044) */
  folder_id: number | null;
  folder_name?: string | null;
  /** present on list rows only */
  access?: FormListAccess;
  /** present on list rows only — everything the Access cell renders */
  sharing?: FormSharing;
  /** present on list rows only (R2): the sparkline, the month count and "{n} new" */
  activity?: FormActivity;
  /** present on list rows only (R2): why a published form is not taking responses, or null */
  closed_reason?: ListClosedReason | null;
}

/** How busy a form has been — what the forms list draws (R2, D14). */
export interface FormActivity {
  /** the last 14 days, oldest first, today last: submissions per day */
  recent: number[];
  /** submissions since the first of this calendar month */
  thisMonth: number;
  /** submissions still in triage `new` */
  new: number;
}
/** The list's derived "Closed": switched off, past its schedule, or full. */
export type ListClosedReason = 'disabled' | 'closed_schedule' | 'closed_quota';
/** The AI builder's month allowance; `allowed` null = unlimited. */
export interface AiAllowance { used: number; allowed: number | null }

export interface FormDetail extends Omit<FormSummary, 'submission_count' | 'last_submission_at' | 'has_draft'> {
  layout: FormLayout;
  rules: FormRule[];
  widgets: FormWidget[];
  draft: Record<string, any> | null;
  /** when the draft last changed (056); null while there is none */
  draft_updated_at?: string | null;
}

export interface SubmissionRow {
  id: number;
  form_id: number;
  submission_id: string;
  data: Record<string, any>;
  meta: Record<string, any>;
  created_at: string;
  /** verified identity, or null for an open/password form */
  submitter?: Submitter | null;
  /** Stripe Connect payment attached to this submission, when the form charges */
  payment?: {
    status: 'pending' | 'paid' | 'canceled'; amountCents: number; currency: string; paidAt?: string;
    /** Stripe's refunded total, when a refund was made from Settings → Payments (R11) */
    refundedCents?: number; refundedAt?: string;
  } | null;
  /** present on list rows: every delivery for this submission, folded into one */
  delivery_summary?: DeliverySummary;
  /** present on list rows of a form that hands somebody a link (W3) */
  follow_up?: FollowUpSummary;
  /** 1 means never edited */
  last_revision?: number;
  edited_at?: string | null;
  /**
   * Set when this submission's document could not be stored — a full account,
   * a template that would not read. The answers were accepted anyway; what is
   * missing is the evidence, which is exactly why the row says so.
   */
  document_error?: string;
  /** triage (P5): where it is and who has it — on the row, never in `data` */
  triage_status?: TriageStatus;
  assignee_user_id?: number | null;
  /** present on list rows: the assignee as a person */
  assignee?: AssigneeRef | null;
  /** an editor's test fill (R9, D11): shown under the Test chip, counted and delivered by nothing */
  is_test?: boolean;
}

// ---- triage (P5) ----
export const TRIAGE_STATUSES = ['new', 'in_review', 'done', 'spam'] as const;
export type TriageStatus = (typeof TRIAGE_STATUSES)[number];

export interface AssigneeRef {
  id: number;
  name: string;
  email: string;
}

export interface SubmissionNote {
  id: number;
  body: string;
  created_at: string;
  /** null once the author's login is gone — the note stays, unsigned */
  author: AssigneeRef | null;
  /** its author, or anyone with Manage on the form */
  canDelete: boolean;
}

export interface TriagePatch {
  status?: TriageStatus;
  /** null clears the assignment */
  assigneeUserId?: number | null;
  /** false skips the "You were assigned …" email; on by default */
  notify?: boolean;
}

export const setTriageApi = (id: number, patch: TriagePatch) =>
  apiFetch<{ success: boolean; triage_status: TriageStatus; assignee: AssigneeRef | null; emailed: boolean }>(
    `/admin/submissions/${id}/triage`, { method: 'PATCH', body: JSON.stringify(patch) });

export const bulkTriageApi = (formId: number | string, ids: number[], patch: TriagePatch) =>
  apiFetch<{ success: boolean; updated: number; emailed: boolean }>(
    `/admin/forms/${formId}/submissions/triage`, { method: 'POST', body: JSON.stringify({ ids, ...patch }) });

export const addNoteApi = (id: number, body: string) =>
  apiFetch<{ success: boolean; note: SubmissionNote }>(`/admin/submissions/${id}/notes`, {
    method: 'POST', body: JSON.stringify({ body }),
  });

export const deleteNoteApi = (id: number, noteId: number) =>
  apiFetch<{ success: boolean }>(`/admin/submissions/${id}/notes/${noteId}`, { method: 'DELETE' });

/** Everyone a submission on this form can be assigned to. */
export const listAssigneesApi = (formId: number | string) =>
  apiFetch<{ success: boolean; users: AssigneeRef[] }>(`/admin/forms/${formId}/assignees`);

/**
 * The two halves of one piece of work, per submission — where the follow-up
 * got to AND the fact that explains it. Assembled by
 * `server/src/services/followUpView.ts`.
 */
export interface FollowUpSummary {
  state: 'awaiting' | 'opened' | 'completed' | 'expired' | null;
  invite: {
    id: number;
    /** the form the link opens */
    formId: number;
    formName: string;
    email: string | null;
    sentAt: string;
    expiresAt: string;
    firstOpenedAt: string | null;
    openedCount: number;
    reminderCount: number;
    lastRemindedAt: string | null;
    nextReminderAt: string | null;
    revoked: boolean;
    /** recovered from the delivery that minted it — the only surviving copy */
    url: string | null;
  } | null;
  child: { id: number; formId: number; submissionId: string; createdAt: string } | null;
  verdicts: { confirmed: number; corrected: number; locked: number } | null;
  /** `count` is null when the draft cannot be counted — say so, do not guess */
  saved: { at: string; count: number | null; label: string | null } | null;
  /** the carrier failed — a delivery problem, not a waiting problem */
  failure: { connectionName: string; message: string; at: string } | null;
  /** a condition kept the carrier from running; nothing was ever sent */
  skipped: { connectionName: string; reason: string } | null;
}

/** What a CHILD submission continues, and what the person said about it. */
export interface ContinuesInfo {
  id: number;
  submissionId: string;
  formId: number;
  formName: string;
  /** an answer off the parent that reads like a name, for the banner */
  summary: string;
  carried: Record<string, { from: unknown; confirmed?: boolean; corrected?: unknown }> | null;
  /** answer key -> label on THIS form, so the record reads in the form's words */
  labels: Record<string, string>;
}

export interface DeliveryRow {
  id: number;
  submission_id: number;
  connection_id: number | null;
  connection_type: string;
  status: 'pending' | 'success' | 'failed' | 'skipped';
  attempts: number;
  last_error: string | null;
  response: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  /** the admin who asked for this send; null = the automatic one at submit */
  triggered_by?: number | null;
}

// Mirrors server/src/repos/connections.ts: the three built-ins are named for
// autocomplete, but 'invite' and every declared integration are types too.
export type ConnectionType = 'monday' | 'webhook' | 'email' | 'invite' | (string & {});
/** One condition on a connection — the Logic page's own shape (035). */
export interface ConnectionCondition {
  field: string;
  op: RuleOp;
  value?: string;
}
export interface ConnectionConditionGroup {
  match?: 'all' | 'any';
  when: ConnectionCondition[];
}

export interface ConnectionRow {
  id: number;
  form_id: number;
  type: ConnectionType;
  name: string;
  config: Record<string, any>;
  enabled: boolean;
  dry_run: boolean;
  /** Position among connections that rank equally (035). */
  sort_order?: number | null;
  /** null/absent = always runs (035). */
  run_when?: ConnectionConditionGroup | null;
}

// ---- forms CRUD ----
export const listForms = () => apiFetch<{
  success: boolean; forms: FormSummary[]; folders: FolderSummary[]; unfiledCount: number;
  /** this month's responses over the forms returned (R2); absent from older servers */
  responsesThisMonth?: number;
  /** the AI builder's month allowance, null on a plan without it (R2); absent from older servers */
  ai?: AiAllowance | null;
}>('/admin/forms');
/** `folderId` is where the rail was pointing — it decides the new form's home
 *  and, through the folder, who can reach it (Q2). */
export const createFormApi = (name: string, folderId?: number | null) =>
  apiFetch<{ success: boolean; form: FormSummary }>('/admin/forms', {
    // Omitted rather than sent as null when there is no folder: a caller that
    // does not care about folders (the follow-up wizard) sends what it always
    // sent, and the server's default is the same answer.
    method: 'POST', body: JSON.stringify({ name, ...(folderId ? { folderId } : {}) }),
  });
/** `myLevel` is what the CALLER may do here — the workspace hides tabs on it. */
/**
 * One form, plus (R3) the published version number — 0 until the first
 * publish — and its activity, for the form bar's `Live v{n}` pill and the
 * "{n} new" badge on Results.
 */
export const getForm = (id: number | string) =>
  apiFetch<{ success: boolean; form: FormDetail; myLevel: Level | null; version?: number; activity?: FormActivity }>(`/admin/forms/${id}`);

// ---- the Publish panel's change list (R3) ----
export type FormChangeOp = 'added' | 'changed' | 'removed';
export type FormChangeKind = 'section' | 'card' | 'widget' | 'field' | 'rule' | 'appearance' | 'document' | 'step' | 'workflow';
export interface FormChange {
  op: FormChangeOp;
  kind: FormChangeKind;
  key: string;
  label: string;
  /** where it is (added / removed) or what changed (changed) */
  detail?: string;
}
/**
 * What publishing would change: the working copy in `draft` (unsaved edits
 * included) against the live definition, grouped and labelled by the server.
 * `since` is the live version the list is relative to.
 */
export const formChanges = (id: number | string, draft: Record<string, unknown>) =>
  apiFetch<{ success: boolean; since: number; count: number; changes: FormChange[] }>(
    `/admin/forms/${id}/changes`, { method: 'POST', body: JSON.stringify({ draft }) }
  );
export const updateFormApi = (id: number | string, body: Record<string, any>) =>
  apiFetch<{ success: boolean; form: FormDetail }>(`/admin/forms/${id}`, { method: 'PUT', body: JSON.stringify(body) });
export const deleteFormApi = (id: number | string) => apiFetch<{ success: boolean }>(`/admin/forms/${id}`, { method: 'DELETE' });
/**
 * `workflow: false` copies the questions and the logic only. Omitted means
 * true — the copy delivers where the original delivers.
 */
export const duplicateFormApi = (id: number | string, opts: { workflow?: boolean } = {}) =>
  apiFetch<{ success: boolean; form: FormSummary }>(`/admin/forms/${id}/duplicate`, { method: 'POST', body: JSON.stringify(opts) });

// ---- starter templates ----
/** What a template needs beyond the free plan before it can take answers (P7). */
export type TemplateNeed = 'payments';
export interface TemplateCategory { id: string; label: string }
export interface FormTemplateSummary {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  needs: TemplateNeed[];
  fieldCount: number;
  pageCount: number;
  /** a two-stage template: the second form the customer finishes by private link */
  followUp?: { name: string; fieldCount: number };
}
/** The gallery's preview: the template shaped for the renderer, nothing stored. */
export interface FormTemplateDetail extends FormTemplateSummary {
  settings: Partial<FormSettingsView>;
  definition: { fields: FieldDefinition[]; layout: FormLayout; rules: FormRule[]; widgets: FormWidget[] };
}
export const listFormTemplates = () =>
  apiFetch<{ success: boolean; categories: TemplateCategory[]; templates: FormTemplateSummary[] }>('/admin/forms/templates');
export const getFormTemplateApi = (templateId: string) =>
  apiFetch<{ success: boolean; template: FormTemplateDetail }>(`/admin/forms/templates/${encodeURIComponent(templateId)}`);
export const createFormFromTemplateApi = (templateId: string, name?: string, folderId?: number | null) =>
  apiFetch<{ success: boolean; form: FormSummary; followUp?: FormSummary }>(`/admin/forms/templates/${encodeURIComponent(templateId)}`, {
    method: 'POST', body: JSON.stringify({ ...(name ? { name } : {}), ...(folderId ? { folderId } : {}) }),
  });

// ---- the AI builder ----
/** Whether "Create with AI" shows at all — the platform console decides. */
export const aiBuilderStatusApi = () =>
  apiFetch<{ success: boolean; available: boolean }>('/admin/forms/ai');
/** Prompt in, draft form out; it opens in the builder like any other draft. */
export const createFormWithAiApi = (prompt: string, name?: string, folderId?: number | null) =>
  apiFetch<{ success: boolean; form: FormSummary }>('/admin/forms/ai', {
    method: 'POST', body: JSON.stringify({ prompt, ...(name ? { name } : {}), ...(folderId ? { folderId } : {}) }),
  });

// ---- the form assistant ("Ask AI" inside a form, 068) ----
/** One field a proposal adds — the AI builder's vocabulary, no payment. */
export interface ProposedFieldSpec {
  key?: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  infoText?: string;
  options?: string[];
  allowOther?: boolean;
  ratingMax?: number;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  matrixRows?: string[];
  matrixColumns?: string[];
  text?: string;
  level?: number;
}
export interface ProposedRule {
  name: string;
  trigger: 'when' | 'always';
  match: 'all' | 'any';
  when: { field: string; op: RuleOp; value?: string }[];
  actions: ({ type: 'show' | 'hide' | 'require' | 'optional' | 'lock' | 'unlock'; targets: string[] } | { type: 'goto'; page: string })[];
}
/** One edit the assistant proposes — mirrors server/src/services/formAssistant.ts. */
export type ProposedChange =
  | { op: 'add_field'; section: string; after?: string; field: ProposedFieldSpec }
  | { op: 'update_field'; key: string; label?: string; required?: boolean; hidden?: boolean;
      placeholder?: string; infoText?: string; options?: string[]; allowOther?: boolean }
  | { op: 'remove_field'; key: string }
  | { op: 'add_section'; id: string; title: string; desc?: string; after?: string; newPage?: boolean }
  | { op: 'update_section'; id: string; title?: string; desc?: string; newPage?: boolean }
  | { op: 'add_rule'; rule: ProposedRule }
  | { op: 'update_rule'; id: string; rule: ProposedRule }
  | { op: 'remove_rule'; id: string }
  | { op: 'set_submit_label'; label: string };
export interface AssistantProposal { summary: string; changes: ProposedChange[] }
export interface AssistantInfo { available: boolean; submissionAccess: boolean; canEdit: boolean }

/** Whether the panel exists here, and what it may do for this person. */
export const assistantInfoApi = (id: number | string) =>
  apiFetch<{ success: boolean } & AssistantInfo>(`/admin/forms/${id}/assistant`);
/** One question. The conversation and the working copy travel with it; the server keeps neither. */
export const askAssistantApi = (
  id: number | string, messages: { role: 'user' | 'assistant'; text: string }[], form: unknown,
) =>
  apiFetch<{ success: boolean; text: string; proposal: AssistantProposal | null }>(`/admin/forms/${id}/assistant`, {
    method: 'POST', body: JSON.stringify({ messages, form }),
  });

// ---- builder ----
export const getFormBuilder = (id: number | string) =>
  apiFetch<{ success: boolean; form: FormDetail; fields: FieldDefinition[]; layout: FormLayout; rules: FormRule[]; widgets: FormWidget[]; draft: Record<string, any> | null; brand?: AccountBrand }>(`/admin/forms/${id}/builder`);

/** One form this one continues, with its published questions — see `parentFields.ts`. */
export interface ParentFormView {
  id: number;
  name: string;
  status: FormStatus;
  layout: FormLayout;
  fields: FieldDefinition[];
  /** Its published repeating containers — a child's templates loop over them as `parent.<scope>`. */
  widgets?: FormWidget[];
  /** The forms this one continues in turn, offered as `parent.parent.<key>`. */
  parents?: ParentFormView[];
}
/**
 * The forms this one continues, resolved: `linking` is every form on the
 * account with a follow-up link step pointing here; `parents` is the
 * continuation's own list (or `ids`, for a list being chosen but not saved).
 */
/**
 * The forms this one continues. `sample` asks for a stand-in ORDER alongside
 * them — the latest real submission of one of the parents, or example answers
 * built from its questions — which is what the builder's Test mode fills its
 * `parent` scope from. It costs a submissions lookup, so only Test mode asks.
 */
export const getFormParents = (id: number | string, ids?: number[], sample?: boolean) => {
  const q = new URLSearchParams();
  if (ids?.length) q.set('ids', ids.join(','));
  if (sample) q.set('sample', '1');
  const qs = q.toString();
  return apiFetch<{
    success: boolean;
    linking: { id: number; name: string; status: FormStatus }[];
    parents: ParentFormView[];
    sample?: { data: Record<string, unknown>; note: string };
  }>(`/admin/forms/${id}/parents${qs ? `?${qs}` : ''}`);
};
/**
 * Save the working copy. `draftUpdatedAt` is the stamp this client last saw
 * (null = it saw no draft); given one, the server refuses with a 409 when the
 * stored draft is newer — another builder saved in between. Left undefined the
 * save is unconditional, as it was before U4.
 */
export const saveFormDraft = (id: number | string, draft: Record<string, any> | null, draftUpdatedAt?: string | null) =>
  apiFetch<{ success: boolean; draftUpdatedAt: string | null }>(`/admin/forms/${id}/draft`, {
    method: 'PUT',
    body: JSON.stringify(draftUpdatedAt === undefined ? { draft } : { draft, draftUpdatedAt }),
  });
export const publishForm = (id: number | string, body: {
  layout: FormLayout; fields: any[]; rules?: FormRule[]; widgets?: FormWidget[];
  /** presentation carried in the draft — written live only by this call */
  settings?: { theme?: FormThemeSettings; submitLabel?: string };
  /** field keys renamed in the draft (R5): the document follows them in the same transaction */
  renames?: { from: string; to: string }[];
}) =>
  apiFetch<{ success: boolean; version: number; layout: FormLayout; fields: FieldDefinition[]; rules: FormRule[]; widgets: FormWidget[] }>(`/admin/forms/${id}/publish`, { method: 'POST', body: JSON.stringify(body) });

// ---- analytics ----
export interface FormAnalyticsData {
  views: number;
  uniqueVisitors: number;
  starts: number;
  submits: number;
  submissions: number;
  pages: { page: number; views: number; uniqueVisitors: number }[];
  /** one entry per calendar day of the window, oldest first (R8) */
  days: { day: string; views: number; uniqueVisitors: number; submits: number }[];
}
export const getFormAnalytics = (id: number | string, days: number) =>
  apiFetch<{ success: boolean; days: number; from: string; to: string; analytics: FormAnalyticsData }>(
    `/admin/forms/${id}/analytics?days=${days}`);

// ---- branding: the logo row (multipart; plan feature `customBranding`) ----
//
// Both answer with the WHOLE row, because both change it: an upload takes a
// position and a removal frees one, and the panel that has to draw three slots
// wants the list, not the one that moved.
export const uploadFormLogo = (id: number | string, file: File, align?: LogoAlign) => {
  const body = new FormData();
  if (align) body.append('align', align);
  body.append('logo', file);
  return apiFetch<{ success: boolean; logo: FormLogo; logos: FormLogo[] }>(
    `/admin/forms/${id}/branding/logo`, { method: 'POST', body });
};
/**
 * Send this form's look up to the account brand. `clearForm` then drops the
 * keys from the form so it inherits what it just gave.
 *
 * `theme` is the builder's UNSAVED draft. It goes with the request because the
 * person clicking is looking at a canvas — promoting the last published
 * colours would send up a look they cannot see. The logo row is not taken from
 * it: logos are written live by their own upload route.
 */
export const promoteFormBranding = (id: number | string, clearForm: boolean, theme?: FormThemeSettings) =>
  apiFetch<{ success: boolean; brand: AccountBrand; theme: FormThemeSettings; notes: string[] }>(
    `/admin/forms/${id}/branding/promote`, {
      method: 'POST', body: JSON.stringify({ clearForm, ...(theme ? { theme } : {}) }),
    });

/** Drop this form's own branded keys so it inherits the account's again. */
export const resetFormBranding = (id: number | string) =>
  apiFetch<{ success: boolean; theme: FormThemeSettings }>(
    `/admin/forms/${id}/branding/reset`, { method: 'POST' });

/** With a url, that one logo; without, every logo the form has. */
export const deleteFormLogo = (id: number | string, url?: string) =>
  apiFetch<{ success: boolean; logos: FormLogo[] }>(
    `/admin/forms/${id}/branding/logo${url ? `?url=${encodeURIComponent(url)}` : ''}`,
    { method: 'DELETE' });

// ---- content images: a picture on the form (multipart; no plan feature — U5) ----
export interface FormAsset { url: string; bytes: number; contentType: string }
export const uploadFormAsset = (id: number | string, file: File) => {
  const body = new FormData();
  body.append('image', file);
  return apiFetch<{ success: boolean; asset: FormAsset }>(`/admin/forms/${id}/assets`, { method: 'POST', body });
};

// ---- the Smart PDF template (multipart; plan feature `smartPdf`) ----
export interface FormPdfTemplate {
  /** Which of the document's PDFs this is — what a `template` part names. */
  id?: string;
  url: string;
  /** A page the app generated, empty, rather than an upload. */
  blank?: boolean;
  name?: string;
  pages: number;
  width: number;
  height: number;
  /** The named AcroForm fields the PDF was found to carry. Absent on a flat design. */
  acroFields?: string[];
  /** AcroForm field name -> form field key. */
  acroMap?: Record<string, string>;
  placements?: unknown[];
  regions?: unknown[];
  flatten?: boolean;
}
/** One part of a composed document. Mirrors DocBlock on the server. */
export type DocBlock =
  /** `templateId` names the PDF; absent = the first. */
  | { type: 'template'; templateId?: string; pages?: number[] }
  /** `hide`: questions left off, by key. */
  | { type: 'answers'; sections?: string[]; hide?: string[]; showHidden?: boolean }
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | {
    type: 'table';
    scope: string;
    columns: { fieldKey: string; label?: string; format?: string; align?: 'left' | 'center' | 'right' }[];
    total?: { column: string; op: 'sum' | 'count'; label?: string };
  }
  | { type: 'terms'; fieldKey: string }
  | { type: 'signature'; fieldKey: string; showAudit?: boolean }
  | { type: 'pageBreak' };

/**
 * This form's document rendered against example answers, as PDF bytes.
 *
 * The whole document travels in the request — the parts as they stand on
 * screen, and the template's mapping and positions — so what comes back is a
 * picture of what a Save would produce rather than of what was last saved.
 * No `mode`: the server derives it from what it is given.
 */
export async function previewFormDocument(
  id: number | string,
  document: { blocks?: DocBlock[]; templates?: FormPdfTemplate[] }
): Promise<Blob> {
  const res = await fetch(`/api/admin/forms/${id}/document/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ document }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Preview failed (${res.status})`;
    try { message = JSON.parse(text).error || message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  return res.blob();
}

type DocumentView = NonNullable<FormSettingsView['document']>;

/** Another PDF for this form's document — or, with `replace`, a new file for one it has (same id). */
export const uploadFormTemplate = (id: number | string, file: File, replace?: string) => {
  const body = new FormData();
  body.append('template', file);
  const q = replace ? `?replace=${encodeURIComponent(replace)}` : '';
  return apiFetch<{ success: boolean; template: FormPdfTemplate; document: DocumentView }>(
    `/admin/forms/${id}/document/template${q}`, { method: 'POST', body });
};
/** A blank page (or pages) to place answers on — or, with `replace`, a new page count for one. */
export const addBlankTemplate = (id: number | string, opts: { pages?: number; replace?: string } = {}) =>
  apiFetch<{ success: boolean; template: FormPdfTemplate; document: DocumentView }>(
    `/admin/forms/${id}/document/template/blank`, { method: 'POST', body: JSON.stringify(opts) });
/** Remove one PDF (and the parts drawing on it); with no `templateId`, every one. */
export const deleteFormTemplate = (id: number | string, templateId?: string) =>
  apiFetch<{ success: boolean; document: DocumentView }>(
    `/admin/forms/${id}/document/template${templateId ? `?id=${encodeURIComponent(templateId)}` : ''}`,
    { method: 'DELETE' });

/**
 * Every PDF a document holds, whichever shape it was stored in — the client
 * twin of `templatesOf` in server/src/types/form.ts: a document saved before
 * there could be several holds one `template`, read as a list of one, id 't1'.
 */
export function templatesOf(document: { template?: FormPdfTemplate; templates?: FormPdfTemplate[] } | undefined | null): FormPdfTemplate[] {
  if (Array.isArray(document?.templates)) {
    return document.templates.filter((t) => t && t.url).map((t, i) => (t.id ? t : { ...t, id: i ? `t${i + 1}` : 't1' }));
  }
  const one = document?.template;
  return one?.url ? [{ ...one, id: one.id || 't1' }] : [];
}

/** The PDF a `template` part draws on: the one it names, or the first. */
export const templateForPart = (templates: FormPdfTemplate[], part: { templateId?: string }): FormPdfTemplate | undefined =>
  part.templateId ? templates.find((t) => t.id === part.templateId) : templates[0];

/**
 * The template filled with example answers, as PDF bytes.
 *
 * Not through `apiFetch`, which parses JSON — this response is a document. The
 * REQUEST carries the template, so the editor previews what is on screen
 * including positions not yet saved.
 */
export async function previewFormTemplate(
  id: number | string, template: FormPdfTemplate
): Promise<Blob> {
  const res = await fetch(`/api/admin/forms/${id}/document/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ template }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Preview failed (${res.status})`;
    try { message = JSON.parse(text).error || message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  return res.blob();
}

// ---- version history ----
export interface FormVersionSummary {
  id: number;
  version: number;
  name: string | null;
  published_by_email: string | null;
  created_at: string;
  /** the snapshot carries the document too — a restore puts it back at once (R3) */
  has_document?: boolean;
}
export interface FormVersionDetail {
  version: number;
  name: string | null;
  created_at: string;
  definition: {
    layout: FormLayout; rules: FormRule[]; widgets: FormWidget[]; fields: FieldDefinition[];
    /** the document as it stood at that publish; absent on versions from before it was snapshotted */
    document?: NonNullable<FormSettingsView['document']>;
  };
}
/**
 * Put a version's DOCUMENT back now (R3). It never rides the draft, so it
 * cannot be restored "as unsaved changes" the way the definition is; the
 * confirm says so. `restored: false` means the version carried no document.
 */
/**
 * The document the Document tab is working on: the draft's, when the draft
 * has one, else the live one. Since 2026-10-09 that tab rides the draft, so
 * anything in the workspace that describes "this form's document" while it
 * is being edited reads this — and `form.settings.document` stays what
 * respondents get until Publish.
 */
export function workingDocumentOf(form: Pick<FormDetail, 'draft' | 'settings'>): FormDetail['settings']['document'] {
  const d = form.draft;
  if (d && typeof d === 'object' && 'document' in d) return (d.document || undefined) as FormDetail['settings']['document'];
  return form.settings?.document;
}

/** Save the Document tab into the draft — it goes live on Publish. */
export const saveDraftDocument = (id: number | string, document: Record<string, unknown>) =>
  apiFetch<{ success: boolean; document: DocumentView | null }>(`/admin/forms/${id}/draft/document`, {
    method: 'PUT', body: JSON.stringify({ document }),
  });

export const restoreVersionDocument = (id: number | string, v: number) =>
  apiFetch<{ success: boolean; restored: boolean }>(`/admin/forms/${id}/versions/${v}/restore-document`, { method: 'POST' });
export const listFormVersions = (id: number | string) =>
  apiFetch<{ success: boolean; current: number; versions: FormVersionSummary[] }>(`/admin/forms/${id}/versions`);
export const getFormVersion = (id: number | string, v: number) =>
  apiFetch<{ success: boolean; version: FormVersionDetail }>(`/admin/forms/${id}/versions/${v}`);
export const renameFormVersion = (id: number | string, v: number, name: string) =>
  apiFetch<{ success: boolean; name: string | null }>(`/admin/forms/${id}/versions/${v}`, { method: 'PATCH', body: JSON.stringify({ name }) });

// ---- submissions ----
export const listFormSubmissions = (id: number | string, page = 1, limit = 50, q = '') =>
  apiFetch<{ success: boolean; submissions: SubmissionRow[]; total: number; page: number; limit: number }>(
    `/admin/forms/${id}/submissions?page=${page}&limit=${limit}&q=${encodeURIComponent(q)}`);
export const getSubmissionDetail = (id: number) =>
  apiFetch<{ success: boolean; submission: SubmissionRow; deliveries: DeliveryRow[] }>(`/admin/submissions/${id}`);
export const deleteSubmissionApi = (id: number) => apiFetch<{ success: boolean }>(`/admin/submissions/${id}`, { method: 'DELETE' });
/** T4: one request for the whole selection; each row dies whole, files included. */
export const bulkDeleteSubmissionsApi = (formId: number | string, ids: number[]) =>
  apiFetch<{ success: boolean; deleted: number }>(`/admin/forms/${formId}/submissions/delete`, {
    method: 'POST', body: JSON.stringify({ ids }),
  });

// ---- connections ----
/**
 * A form's steps. `draft` is the Workflow tab's view — the steps as its draft
 * has them (085). Everything else reads what is live.
 */
export const listFormConnections = (formId: number | string, opts: { draft?: boolean } = {}) =>
  apiFetch<{ success: boolean; connections: ConnectionRow[] }>(`/admin/forms/${formId}/connections${opts.draft ? '?draft=1' : ''}`);
export const createConnectionApi = (formId: number | string, body: Record<string, any>) =>
  apiFetch<{ success: boolean; connection: ConnectionRow }>(`/admin/forms/${formId}/connections`, { method: 'POST', body: JSON.stringify(body) });
/** Rewrite the whole order — see the route's note on why it is not move-one. */
export const reorderConnectionsApi = (formId: number | string, ids: number[]) =>
  apiFetch<{ success: boolean; connections: ConnectionRow[] }>(
    `/admin/forms/${formId}/connections/order`,
    { method: 'PUT', body: JSON.stringify({ ids }) });
export const updateConnectionApi = (id: number, body: Record<string, any>) =>
  apiFetch<{ success: boolean; connection: ConnectionRow }>(`/admin/connections/${id}`, { method: 'PUT', body: JSON.stringify(body) });
/** A second connection with this one's settings — made server-side, where the secrets are. */
export const copyConnectionApi = (id: number) =>
  apiFetch<{ success: boolean; connection: ConnectionRow }>(`/admin/connections/${id}/copy`, { method: 'POST' });
export const deleteConnectionApi = (id: number) => apiFetch<{ success: boolean }>(`/admin/connections/${id}`, { method: 'DELETE' });
export const testConnectionApi = (id: number, body: { config?: Record<string, any>; sample?: 'latest' | 'example' } = {}) =>
  apiFetch<{ success: boolean; sampleSource?: string; preview?: Record<string, any>; error?: string }>(
    `/admin/connections/${id}/test`, { method: 'POST', body: JSON.stringify(body) });
// ---- the workflow graph (043) ----
// Mirrors server/src/services/delivery/graphTypes.ts. The two workspaces share
// no code, so this is a hand-mirrored type in the same way ConnectionRow is;
// shared/graphCases.json is what keeps the two graph implementations honest.
export type WorkflowNodeKind = 'start' | 'deliver' | 'branch' | 'merge' | 'mint' | 'chase' | 'end';

export interface WorkflowNode {
  id: string;
  kind: WorkflowNodeKind;
  /** deliver and mint: the connection holding the configuration. */
  connectionId?: number;
  /** branch, chase, end: settings with nowhere else to live. */
  config?: Record<string, any>;
  x: number;
  y: number;
}
export interface WorkflowEdge { from: string; port: string; to: string }
export interface WorkflowGraph { nodes: WorkflowNode[]; edges: WorkflowEdge[] }

export interface StoredWorkflow {
  graph: WorkflowGraph;
  /** false = the connection list is still what runs this form. */
  enabled: boolean;
  updatedAt: string;
}

export interface WorkflowResponse {
  success: boolean;
  /** null when this form has never adopted one. */
  workflow: StoredWorkflow | null;
  /** What adoption would draw — computed, never stored. */
  proposal: WorkflowGraph;
  /** proposal node id -> where that node came from. */
  provenance: Record<string, string>;
}

/** `draft`: the drawing the Workflow tab is working on, when the draft has one (085). */
export const getFormWorkflow = (formId: number | string, opts: { draft?: boolean } = {}) =>
  apiFetch<WorkflowResponse>(`/admin/forms/${formId}/workflow${opts.draft ? '?draft=1' : ''}`);

/**
 * Save the drawing into the DRAFT — it goes live on Publish (085). `first`
 * is the one live write left: the drawing of what a form already does, made
 * the first time its Workflow tab is opened.
 */
export const saveFormWorkflowApi = (formId: number | string, graph: WorkflowGraph, opts: { first?: boolean } = {}) =>
  apiFetch<{ success: boolean; workflow: StoredWorkflow; warnings: { message: string; nodeId?: string }[] }>(
    `/admin/forms/${formId}/workflow`,
    { method: 'PUT', body: JSON.stringify({ graph, ...(opts.first ? { first: true } : {}) }) });

export interface DryStep {
  nodeId: string;
  connectionId?: number;
  outcome: 'would-send' | 'skipped' | 'off' | 'broken';
  note?: string;
  preview?: Record<string, any>;
}
export interface DryRun {
  sampleSource: string;
  taken: string[];
  ports: string[];
  endedAt: string | null;
  steps: DryStep[];
}

/**
 * Try a workflow without running it.
 *
 * The graph goes in the body so a drawing can be tried before it is saved —
 * which is the point, on a page where saving is going live.
 */
export const testWorkflowApi = (
  formId: number | string, graph: WorkflowGraph, sample: 'latest' | 'example'
) => apiFetch<{ success: boolean; run: DryRun; data: Record<string, unknown> }>(
  `/admin/forms/${formId}/workflow/test`,
  { method: 'POST', body: JSON.stringify({ graph, sample }) });

export const retryDeliveryApi = (id: number) =>
  apiFetch<{ success: boolean; delivery: DeliveryRow }>(`/admin/deliveries/${id}/retry`, { method: 'POST' });

// ---- who can open a form ----
export interface AccessProvider {
  id: number; name: string; protocol: ProviderProtocol; status: ProviderStatus;
  claimNames: string[]; allowedDomains: string[];
}
export interface FormAccessResponse {
  success: boolean;
  access: FormAccess;
  effectiveMode: FormAccess['mode'];
  hasPassword: boolean;
  /** the form asks for single sign-on, and the account's plan does not include it right now */
  ssoOff?: boolean;
  accountSlug: string;
  /** whether the platform has Turnstile keys — without them the captcha switch is shown disabled (U6) */
  captchaConfigured?: boolean;
  spam?: FormSpamSettings;
  /** the link-prefill mapping (P1), and the origin the link builder composes from */
  prefill?: FormPrefillSettings;
  publicBaseUrl?: string;
  /** the account's live custom domain as an origin, else publicBaseUrl */
  formLinkBaseUrl?: string;
  providers: AccessProvider[];
}

export const getFormAccess = (formId: number | string) =>
  apiFetch<FormAccessResponse>(`/admin/forms/${formId}/access`);

export const saveFormAccess = (formId: number | string, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; access: FormAccess; hasPassword: boolean; prefill?: FormPrefillSettings }>(
    `/admin/forms/${formId}/access`, { method: 'PATCH', body: JSON.stringify(body) });

/** A signed prefill link's query string (P1 / A4) for the values typed into the builder — the SAVED mapping is what gets signed. */
export const signPrefillLink = (formId: number | string, values: Record<string, string>) =>
  apiFetch<{ success: boolean; query: string }>(
    `/admin/forms/${formId}/prefill/sign`, { method: 'POST', body: JSON.stringify({ values }) });

/** Bump the signing key: every signed link issued so far stops locking anything. */
export const rotatePrefillKey = (formId: number | string) =>
  apiFetch<{ success: boolean; prefill: FormPrefillSettings }>(
    `/admin/forms/${formId}/prefill/rotate`, { method: 'POST', body: '{}' });


// ---- the submissions table ----
export interface SubmissionColumn {
  id: string;
  label: string;
  key?: string;
  kind: 'meta' | 'field' | 'group';
  type: 'text' | 'number' | 'date' | 'choice' | 'identity' | 'deliveries';
  options?: string[];
  group: string;
  /** stored data has this key but the form no longer asks for it */
  orphan?: boolean;
  /** always shown, cannot be turned off */
  locked?: boolean;
  numeric?: boolean;
}

export interface SubmissionPage {
  success: boolean;
  submissions: SubmissionRow[];
  total: number;
  page: number;
  limit: number;
  /** form-wide, whatever the filters: the badges' own numbers */
  counts: { needsAttention: number; spam?: number; test?: number; waiting?: number; new?: number; mine?: number };
}

/**
 * Filters go on the query string so the whole view is in the URL — a triage
 * link pasted into chat opens the same rows for whoever clicks it.
 */
export function submissionQueryString(f: SubmissionFilters & {
  page?: number; limit?: number; sort?: string; dir?: string;
}): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.delivery && f.delivery !== 'any') p.set('delivery', f.delivery);
  if (f.connectionId) p.set('connectionId', String(f.connectionId));
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.match === 'any') p.set('match', 'any');
  if (f.conditions?.length) p.set('conditions', JSON.stringify(f.conditions));
  if (f.page && f.page > 1) p.set('page', String(f.page));
  if (f.limit) p.set('limit', String(f.limit));
  if (f.sort) p.set('sort', f.sort);
  if (f.dir) p.set('dir', f.dir);
  return p.toString();
}

/**
 * A signed, short-lived test link for the builder's Test mode (R9, D11): the
 * live form opened with `?test=<token>` saves what is submitted as a test.
 * Refused (400) on a form that has never been published.
 */
export const mintTestTokenApi = (formId: number | string) =>
  apiFetch<{ success: boolean; token: string; expiresAt: string }>(`/admin/forms/${formId}/test-token`, { method: 'POST' });

export const querySubmissionsApi = (formId: number | string, query: string) =>
  apiFetch<SubmissionPage>(`/admin/forms/${formId}/submissions${query ? `?${query}` : ''}`);

export const getSubmissionColumns = (formId: number | string) =>
  apiFetch<{ success: boolean; columns: SubmissionColumn[]; selected: string[]; defaults: string[] }>(
    `/admin/forms/${formId}/submissions/columns`);

export const saveSubmissionColumns = (formId: number | string, columns: string[]) =>
  apiFetch<{ success: boolean; selected: string[] }>(`/admin/forms/${formId}/submissions/columns`, {
    method: 'PUT', body: JSON.stringify({ columns }),
  });

// ---- saved views ----
export interface SubmissionView {
  id: number;
  form_id: number;
  name: string;
  shared: boolean;
  config: ViewConfig;
  created_by: number | null;
}

export const listViewsApi = (formId: number | string) =>
  apiFetch<{ success: boolean; views: SubmissionView[] }>(`/admin/forms/${formId}/views`);

export const createViewApi = (formId: number | string, body: { name: string; shared: boolean; config: ViewConfig }) =>
  apiFetch<{ success: boolean; view: SubmissionView }>(`/admin/forms/${formId}/views`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const updateViewApi = (formId: number | string, viewId: number, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; view: SubmissionView }>(`/admin/forms/${formId}/views/${viewId}`, {
    method: 'PATCH', body: JSON.stringify(body),
  });

export const deleteViewApi = (formId: number | string, viewId: number) =>
  apiFetch<{ success: boolean }>(`/admin/forms/${formId}/views/${viewId}`, { method: 'DELETE' });

// ---- one submission: revisions, editing, re-send ----
export interface RevisionSummary {
  revision: number;
  changed_by: number | null;
  changed_by_email: string | null;
  /** who wrote it: somebody in the console, or the respondent (revision 1, and a P6 edit) */
  author?: 'admin' | 'respondent';
  created_at: string;
}

export interface ResendConnection {
  id: number;
  name: string;
  type: string;
  enabled: boolean;
  dryRun: boolean;
  status: 'success' | 'failed' | 'pending' | 'skipped' | 'never';
  lastAttemptAt: string | null;
  attempts: number;
  error: string | null;
  /** the config was edited after the failure — usually why a re-send will work */
  configChangedAt: string | null;
  preselect: boolean;
}

export interface SubmissionDetailResponse {
  success: boolean;
  submission: SubmissionRow;
  deliveries: DeliveryRow[];
  revisions: RevisionSummary[];
  /** answers taken from the signed-in account; not editable */
  lockedFields: string[];
  connections: ResendConnection[];
  /** on a parent: what it is waiting on (only when the form sends invites) */
  followUp?: FollowUpSummary;
  /** on a child: what it continues, and the confirmation record */
  continues?: ContinuesInfo;
  /**
   * The document(s) this submission has: the copy that was SENT, and any
   * fresh copy an admin made after editing it. Empty on a submission taken
   * before documents were stored — the drawer says so rather than hiding it.
   */
  documents?: SubmissionDocumentRow[];
  /** triage (P5): the team's notes, the assignee, and how far this caller may go on the form */
  notes?: SubmissionNote[];
  assignee?: AssigneeRef | null;
  level?: 'view' | 'edit' | 'manage' | null;
}

export const getSubmissionDetailFull = (id: number | string) =>
  apiFetch<SubmissionDetailResponse>(`/admin/submissions/${id}`);

export const getSubmissionRevision = (id: number | string, rev: number) =>
  apiFetch<{ success: boolean; revision: { revision: number; data: Record<string, any>; created_at: string } }>(
    `/admin/submissions/${id}/revisions/${rev}`);

/** 204 with no body when nothing changed — no revision is written. */
export async function updateSubmissionApi(id: number, data: Record<string, unknown>) {
  const res = await fetch(`/api/admin/submissions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ data }),
  });
  if (res.status === 204) return { unchanged: true as const };
  const body = await res.json();
  if (!res.ok || body.success === false) throw new Error(body.error || 'Could not save the edit.');
  return { unchanged: false as const, ...body } as {
    unchanged: false; submission: SubmissionRow; revision: number; changed: string[];
  };
}

export interface ResendResult {
  connectionId: number;
  connectionName: string;
  connectionType: string;
  status: 'success' | 'failed' | 'skipped' | 'preview';
  error?: string;
  response?: Record<string, any>;
}

export const resendSubmissionApi = (id: number, connectionIds: number[], dryRun = false) =>
  apiFetch<{ success: boolean; dryRun: boolean; results: ResendResult[] }>(`/admin/submissions/${id}/resend`, {
    method: 'POST', body: JSON.stringify({ connectionIds, dryRun }),
  });

/**
 * Send the follow-up link again.
 *
 * `same` is what the reminders do — anything already saved against the link
 * stays. `fresh` revokes the old links first, so the one the customer is
 * holding stops working the moment this returns.
 */
export const resendInviteApi = (id: number, mode: 'same' | 'fresh') =>
  apiFetch<{ success: boolean; mode: string; deliveries: DeliveryRow[] }>(
    `/admin/submissions/${id}/resend-invite`, { method: 'POST', body: JSON.stringify({ mode }) });

export const bulkResendApi = (formId: number | string, submissionIds: number[], connectionIds: number[]) =>
  apiFetch<{ success: boolean; queued: number }>(`/admin/forms/${formId}/submissions/resend`, {
    method: 'POST', body: JSON.stringify({ submissionIds, connectionIds }),
  });

// ---- the document a submission sent (D0) ----

/**
 * One stored document. `url` is null on a row that records a FAILURE — the
 * render or the store did not happen, the submission was accepted anyway, and
 * somebody has to be able to see that and retry it.
 */
export interface SubmissionDocumentRow {
  submission_id: number;
  kind: 'sent' | 'rerendered';
  url: string | null;
  bytes: number;
  pages: number;
  document_hash: string | null;
  error: string | null;
  rendered_at: string;
}

/**
 * Make a document for this submission now.
 *
 * Which job it does is the SERVER's call, not a flag from here: with nothing
 * stored (or a stored failure) it writes the sent copy, and with a real one
 * already there it writes a fresh copy beside it. The sent copy is never
 * overwritten — that is the whole reason submissions store their documents.
 */
export const renderSubmissionDocument = (id: number | string) =>
  apiFetch<{ success: boolean; kind: 'sent' | 'rerendered'; document: SubmissionDocumentRow }>(
    `/admin/submissions/${id}/document`, { method: 'POST' }
  );

// ---- the Summary tab (P2) ----
export type SummaryResult =
  | { kind: 'choice'; multi: boolean; items: { value: string; label: string; count: number }[]; other: number }
  | { kind: 'rating'; max: number; items: { value: number; count: number }[]; mean: number | null }
  | {
      kind: 'number'; count: number; min: number | null; max: number | null; mean: number | null;
      buckets: { from: number; to: number; count: number }[];
    }
  | { kind: 'date'; days: { day: string; count: number }[] }
  | { kind: 'yesno'; yes: number; no: number }
  | { kind: 'text'; latest: string[] }
  /** P3: a linear scale — like a rating, but the run may start at 0 (an NPS) */
  | { kind: 'scale'; min: number; max: number; items: { value: number; count: number }[]; mean: number | null }
  /** P3: counts per row × column; `counts[row][column]` */
  | {
      kind: 'matrix'; multi: boolean;
      rows: { value: string; label: string }[]; columns: { value: string; label: string }[];
      counts: number[][];
    }
  /** P3: each option's average position (1 = ranked first), best first */
  | { kind: 'ranking'; items: { value: string; label: string; mean: number | null; count: number; first: number }[] };

export interface QuestionSummary {
  key: string;
  label: string;
  type: string;
  scope: string;
  section: string;
  options?: { value: string; text: string }[];
  ratingMax?: number;
  scale?: { min: number; max: number };
  matrix?: { rows: { value: string; text: string }[]; columns: { value: string; text: string }[]; multi: boolean };
  /** rows (or instances) with a non-blank answer */
  answered: number;
  /** repeating scopes: how many instances the counted rows held */
  instances?: number;
  scopeLabel?: string;
  result: SummaryResult;
}

export interface FormSummaryData {
  success: boolean;
  total: number;
  /** true when more rows matched than the server counts at once — the newest were counted */
  sampled: boolean;
  questions: QuestionSummary[];
  filters: SubmissionFilters;
}

/** `query` is the table's own query string, plus `view=` for a saved view. */
export const getFormSummary = (formId: number | string, query = '') =>
  apiFetch<FormSummaryData>(`/admin/forms/${formId}/summary${query ? `?${query}` : ''}`);

/**
 * The selected submissions' PDFs as one zip. A POST, so the ids ride in the
 * body rather than a URL that 200 of them would not fit in.
 */
export async function downloadSubmissionsZip(formId: number | string, ids: number[]): Promise<Blob> {
  const res = await fetch(`/api/admin/forms/${formId}/submissions/export.zip`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = `Download failed (${res.status})`;
    try { message = JSON.parse(text).error || message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  return res.blob();
}

/** Hand the browser a file it fetched — a synthetic download link, then tidied. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---- Automate › Runs (R6) ----
export type RunStatus = 'failed' | 'waiting' | 'done';
export type RunFilter = 'all' | RunStatus;
export type RunStepState = 'done' | 'failed' | 'waiting' | 'pending' | 'skipped' | 'off' | 'unrecorded';
export interface RunStep {
  nodeId: string;
  kind: WorkflowNodeKind;
  title: string;
  state: RunStepState;
  detail: string;
  connectionId?: number;
  connectionType?: string;
  deliveryId?: number;
  attempts?: number;
  error?: string | null;
  /** a failed delivery whose connection still exists — `retryDeliveryApi` takes it */
  retryable: boolean;
  /** from a step not on this path as the workflow is drawn now */
  stray?: boolean;
}
export interface RunSummary {
  id: number;
  submissionId: string;
  label: string;
  startedAt: string;
  status: RunStatus;
  statusText: string;
  path: string;
  pathLabels: string[];
  steps: RunStep[];
  endedAt: string | null;
}
export interface RunCounts { all: number; failed: number; waiting: number; done: number }
export interface RunsResponse {
  success: boolean;
  runs: RunSummary[];
  counts: RunCounts;
  total: number;
  page: number;
  limit: number;
  filter: RunFilter;
  replayedAgainst: 'saved' | 'implied';
}
/** One run per submission, newest first; the status decides the tab (see the route). */
export const listRunsApi = (formId: number | string, opts: { status?: RunFilter; page?: number; limit?: number } = {}) => {
  const qs = new URLSearchParams();
  if (opts.status && opts.status !== 'all') qs.set('status', opts.status);
  if (opts.page && opts.page > 1) qs.set('page', String(opts.page));
  if (opts.limit) qs.set('limit', String(opts.limit));
  const q = qs.toString();
  return apiFetch<RunsResponse>(`/admin/forms/${formId}/runs${q ? `?${q}` : ''}`);
};
